/**
 * v1.2 Batch 2-A：销售流程进度条 + L7 决策横幅
 *
 * 客户详情页顶部展示 8 步流程（1 客户管理 → 8 提交订单）。
 * 已完成 = 绿、当前 = 蓝高亮、未到 = 灰；点节点跳对应路由。
 *
 * L7 客户尚未启动销售流程时，顶部横幅引导销售员选「有/无定金」。
 *
 * 严格对齐设计 token：圆角 / 间距 / 色用 var(--*)；字号 14/13/12；
 * 圆角 8（按钮）/ 12（卡片）/ 4（chip）；间距 4/8/12/16/24。
 */
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Modal, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn } from './ui';
import { ContractCreateModal } from './BizCreateModals';

export type SalesFlowStep =
  | '5_deposit'
  | '3_contract'
  | '4_invoice'
  | '6_shipment'
  | '7_materials'
  | '8_order'
  | 'completed'
  | null
  | undefined;

interface StepDef {
  num: number;
  key: string;          // matches SalesFlowStep prefix
  label: string;
  route: string;        // navigate target
}

// 8 步与 Layout STAGES 对齐。流转理顺：显示步号顺正为 ③合同④收款⑤发票⑥教材⑦资料⑧订单
//   （key 字符串 5_deposit/4_invoice 不变=零迁移；这里把收款显号设 4、发票显号设 5，并把收款排在发票前）
const ALL_STEPS: StepDef[] = [
  { num: 1, key: '1_customer',  label: '客户管理',  route: '/customers' },
  { num: 2, key: '2_followup',  label: '跟进记录',  route: '/follow-ups' },
  { num: 3, key: '3_contract',  label: '签订合同',  route: '/contracts' },
  { num: 4, key: '5_deposit',   label: '收款',      route: '/payments' },
  { num: 5, key: '4_invoice',   label: '发票',      route: '/invoices' },
  { num: 6, key: '6_shipment',  label: '教材邮寄',  route: '/shipments' },
  { num: 7, key: '7_materials', label: '学员资料',  route: '/materials' },
  { num: 8, key: '8_order',     label: '提交订单',  route: '/orders' },
];

// 项4(C批②)重排 + 全款补通：合同在前、签约后统一进收款步(5_deposit)，不再按定金分流。
// 有定金/无定金路径相同——只是收款方式不同(分期 deposit / 一次性 full)，故两条数组一致。
const ORDER_WITH_DEPOSIT    = ['3_contract', '5_deposit', '4_invoice', '6_shipment', '7_materials', '8_order'];
const ORDER_WITHOUT_DEPOSIT = ['3_contract', '5_deposit', '4_invoice', '6_shipment', '7_materials', '8_order'];

function stepIndex(order: string[], step: string): number {
  return order.indexOf(step);
}

interface Props {
  customerId: number | string;
  currentStage?: string | null;
  salesFlowStep?: SalesFlowStep;
  depositChosen?: 'with' | 'without' | null;
  // 项3/4：合同内部审批态（none/pending/approved/rejected）+ 驳回理由，用于「合同步」中间态展示
  contractApprovalStatus?: string | null;
  contractRejectionReason?: string | null;
  onRefresh: () => void;
  // 第2批合页联动：点节点 → 滚动定位 + 展开本页对应卡段（由 CustomerDetail 传入）。
  //   未传时退化为原行为（节点仍可点、走列表跳转）。
  //   route 透传：无锚点步（1/2 跟进、4 发票无独立合页段时）由 CustomerDetail 退化跳对应列表。
  onStepClick?: (key: string, route?: string) => void;
}

export function SalesFlowProgress({
  customerId, currentStage, salesFlowStep, depositChosen,
  contractApprovalStatus, contractRejectionReason, onRefresh, onStepClick,
}: Props) {
  const nav = useNavigate();
  // 第2批收口：进度条节点只保留「合同(step3)」的 inline 新建/重建；收款/教材/资料/订单
  //   操作已全部移至合页卡，节点点击联动到对应合页段，故 openModal 仅剩 '3'。
  const [openModal, setOpenModal] = useState<null | '3'>(null);

  // L7/L8 客户但还没启动流程 → 显示决策横幅（死锁修复：current_stage 越过 L7 到 L8 时也放行启动，与后端 sales-flow start 校验对称）
  const needsDecision = ['L7', 'L8'].includes(currentStage ?? '') && !salesFlowStep;
  // 已完成
  const done = salesFlowStep === 'completed';
  // 流程进行中
  const inFlow = !!salesFlowStep && !done;

  if (needsDecision) {
    return <L7DecisionBanner customerId={customerId} onStarted={onRefresh} />;
  }

  // 当前 step 在状态流里的位置（仅用于高亮）
  const order = depositChosen === 'with' ? ORDER_WITH_DEPOSIT : ORDER_WITHOUT_DEPOSIT;
  const currentIdx = inFlow ? stepIndex(order, salesFlowStep as string) : -1;

  // 判断某 step 状态：completed / current / pending
  const stepState = (key: string): 'done' | 'current' | 'pending' => {
    if (key === '1_customer' || key === '2_followup') {
      // 1/2 是跟进阶段，customer 到了 L7+ 即视为完成
      return ['L7', 'L8'].includes(currentStage ?? '') || inFlow || done ? 'done' : 'current';
    }
    if (done) return 'done';
    if (!inFlow) return 'pending';
    const i = stepIndex(order, key);
    if (i < 0) return 'pending';
    if (i < currentIdx) return 'done';
    if (i === currentIdx) return 'current';
    return 'pending';
  };

  // 过滤：如果 depositChosen='without'，第 5 步不在流程里 → 显示但灰
  return (
    <div
      style={{
        // inc6#3：窄屏(~1061px+详情面板)原 overflowX:auto 不产生横滚、⑤-⑧ 被挤断不可见。
        //   改 flexWrap:'wrap'——8 步换行成两排、全可见、无需横滚（连接线随之换行，纯视觉、可接受）。
        display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', rowGap: 10,
        padding: '12px 16px', marginBottom: 16,
        background: 'var(--card-bg, #FFFFFF)',
        border: '1px solid var(--border-default, #E5E6EB)',
        borderRadius: 12,
      }}
    >
      {ALL_STEPS.map((s, idx) => {
        const state = stepState(s.key);
        return (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              // 第2批合页联动：优先调 onStepClick（滚动定位 + 展开本页对应卡段）；
              //   未传 onStepClick（旧用法）退化为原「跳列表页带 customerId 过滤」行为。
              onClick={() => {
                // 传 route：无锚点步（1/2 跟进、4 发票等无合页段时）由 CustomerDetail 退化跳列表。
                if (onStepClick) onStepClick(s.key, s.route);
                else nav(`${s.route}?customerId=${customerId}`);
              }}
              title={s.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 10px',
                border: 'none', cursor: 'pointer',
                borderRadius: 8,
                fontSize: 13,
                background:
                  state === 'current' ? 'var(--primary-bg, #EAF2FF)' :
                  state === 'done'    ? 'rgba(25, 188, 156, 0.10)' :
                                        'transparent',
                color:
                  state === 'current' ? 'var(--primary, #3370FF)' :
                  state === 'done'    ? 'var(--success, #19BC9C)' :
                                        'var(--text-3, #8F959E)',
                fontWeight: state === 'current' ? 500 : 400,
              }}
            >
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  fontSize: 12,
                  background:
                    state === 'current' ? 'var(--primary, #3370FF)' :
                    state === 'done'    ? 'var(--success, #19BC9C)' :
                                          'var(--border-default, #E5E6EB)',
                  color: state === 'pending' ? 'var(--text-3, #8F959E)' : '#FFFFFF',
                }}
              >
                {state === 'done' ? '✓' : s.num}
              </span>
              {s.label}
            </button>
            {/* 第2批：节点点击改为页内联动后，列表跳转降级为节点旁「↗」小链接次要入口（弱化保留）。
                仅当启用联动（onStepClick 存在）时显示，避免与旧用法重复。 */}
            {onStepClick && (
              <span
                role="button"
                title="在列表页查看本步骤"
                onClick={(e) => { e.stopPropagation(); nav(`${s.route}?customerId=${customerId}`); }}
                style={{
                  cursor: 'pointer', fontSize: 11, color: 'var(--text-3, #8F959E)',
                  padding: '0 2px', lineHeight: 1, userSelect: 'none',
                }}
              >↗</span>
            )}
            {idx < ALL_STEPS.length - 1 && (
              <span style={{
                width: 12, height: 1,
                background: 'var(--border-default, #E5E6EB)',
              }} />
            )}
          </div>
        );
      })}
      {done && (
        <span style={{
          marginLeft: 12, padding: '4px 10px',
          background: 'rgba(25, 188, 156, 0.12)',
          color: 'var(--success, #19BC9C)',
          fontSize: 12, fontWeight: 500,
          borderRadius: 4, flexShrink: 0,
        }}>
          ✓ 全流程已完成 · 教务接入中
        </span>
      )}
      {/* 第2批收口：进度条节点只保留「合同(step3)」inline 操作；收款/教材/资料/订单
          的新建与提交已全部移至下方合页卡，点对应进度条节点即联动到合页段，进度条不再重复入口。 */}
      {inFlow && !done && (() => {
        const curStep = (salesFlowStep ?? '').split('_')[0]; // '3' / '5' ...
        // 仅合同步(3)在进度条上保留 inline 操作；5/6/7/8 已全部下沉合页卡 → 这里不渲染。
        if (curStep !== '3') return null;
        // 项3/4：合同步（step 3）按审批态显中间态——
        //   pending → 合同已提交、待主管审核，不显「+ 新建」（防重复建第二份合同）；
        //   rejected → 显驳回理由 + 允许重新创建；none/approved → 正常「+ 新建合同」。
        if (contractApprovalStatus === 'pending') {
          return (
            <span style={{
              marginLeft: 8, padding: '4px 10px', flexShrink: 0,
              background: 'var(--warning-bg, #FFF7E6)', color: 'var(--warning, #FA8C16)',
              fontSize: 12, fontWeight: 500, borderRadius: 4,
            }}>
              ⏳ 合同已提交 · 待主管审核（通过后解锁后续）
            </span>
          );
        }
        if (contractApprovalStatus === 'rejected') {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 8, flexShrink: 0 }}>
              <span style={{
                padding: '4px 10px',
                background: 'var(--danger-bg, #FFF1F0)', color: 'var(--danger, #F53F3F)',
                fontSize: 12, fontWeight: 500, borderRadius: 4,
              }}>
                ❌ 合同被驳回{contractRejectionReason ? `：${contractRejectionReason}` : ''}
              </span>
              <Btn kind="primary" onClick={() => setOpenModal('3')}>重新创建合同</Btn>
            </span>
          );
        }
        // none/approved：尚未提交或已通过待推进——显「+ 新建合同」（开 ContractCreateModal）。
        return (
          <Btn kind="primary" onClick={() => setOpenModal('3')}>
            + 新建合同
          </Btn>
        );
      })()}
      <ContractCreateModal open={openModal === '3'} onClose={() => { setOpenModal(null); onRefresh(); }} customerId={Number(customerId)} />
    </div>
  );
}

function L7DecisionBanner({ customerId, onStarted }: { customerId: number | string; onStarted: () => void }) {
  const qc = useQueryClient();
  const [picking, setPicking] = useState<'with' | 'without' | null>(null);

  const start = useMutation({
    mutationFn: (depositChosen: 'with' | 'without') =>
      $.post(`/customers/${customerId}/sales-flow/start`, { depositChosen }),
    onSuccess: (_r, depositChosen) => {
      message.success(`已启动销售流程（收款方式：${depositChosen === 'with' ? '分期/定金' : '一次性全款'}）`);
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
      onStarted();
    },
    onError: (e: any) => message.error(e?.message ?? '启动失败'),
  });

  const confirm = (depositChosen: 'with' | 'without') => {
    setPicking(depositChosen);
    Modal.confirm({
      title: depositChosen === 'with' ? '确认：分期（先收定金）' : '确认：一次性全款',
      content: depositChosen === 'with'
        ? '收款方式＝分期：先进入「3 签订合同」，提交后需主管审核通过；签约后进入「5 收款」按定金分期收，收齐再开票。'
        : '收款方式＝一次性全款：先进入「3 签订合同」，提交后需主管审核通过；签约后进入「5 收款」一次性收全款，收齐再开票。',
      okText: '确认启动',
      cancelText: '取消',
      onOk: () => start.mutate(depositChosen),
      onCancel: () => setPicking(null),
    });
  };

  return (
    <div
      style={{
        padding: '16px 20px', marginBottom: 16,
        background: 'linear-gradient(135deg, #EAF2FF 0%, #F0E9FF 100%)',
        border: '1px solid var(--primary, #3370FF)',
        borderRadius: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1, #1F2329)' }}>
          🎯 客户已到 L7 — 请选择收款方式
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2, #4E5969)', marginTop: 4 }}>
          统一路径：3 合同 → 4 收款 → 5 发票 → 6 教材 → 7 资料 → 8 订单。仅收款方式不同：分期(先收定金) / 一次性全款
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <Btn kind="default" disabled={start.isPending} onClick={() => confirm('without')}>
          一次性全款
        </Btn>
        <Btn kind="primary" disabled={start.isPending} onClick={() => confirm('with')}>
          分期（定金）
        </Btn>
      </div>
    </div>
  );
}
